import {
  toSeoProps,
  type UmbrellaSeoRoutes,
} from '@saas-pages/shared/public/seo'
import type { SeoProps } from '@saas-pages/shared/public/seo'
import branding from '../../branding.config'
import { enTranslations } from '../shared/i18n/en'
import { ruTranslations } from '../shared/i18n/ru'
import { DEFAULT_LOCALE, type Locale } from '../shared/i18n/locale'
import { buildQrToolRouteSchemas, type QrToolRouteKey } from './toolSchemas'

export type RouteKey =
  | 'index'
  | 'qr-generator'
  | 'barcode-generator'
  | 'barcode-scanner'
  | 'qr-scanner'
  | 'blog'

export const seoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  domain: branding.domain,
  routes: {
    index: {
      path: '/',
      title: 'QR и штрихкод инструменты онлайн - qrka.ru',
      description:
        'Бесплатные инструменты qrka.ru: создать QR-код, сгенерировать штрихкод EAN/UPC/Code 128 и считать QR-код с фото или камеры.',
      changefreq: 'weekly',
      priority: 1,
      jsonLd: {
        mode: 'none',
        reason: 'Umbrella catalog page; concrete QR/barcode utility pages carry tool JSON-LD.',
      },
    },
    'qr-generator': {
      path: '/qr-kod',
      title: 'Генератор QR-кодов онлайн - PNG и SVG бесплатно - qrka.ru',
      description:
        'Создайте статический QR-код для URL, текста, Wi-Fi, vCard, email, SMS или телефона. Настройте вид и скачайте PNG или SVG локально в браузере.',
      changefreq: 'monthly',
      priority: 1,
      jsonLd: {
        mode: 'required',
        schemas: buildQrToolRouteSchemas('/qr-kod', ruTranslations, 'qr-generator'),
      },
    },
    'barcode-generator': {
      path: '/generator-shtrih-kodov',
      title: 'Генератор штрих-кодов онлайн - EAN, UPC, Code 128 - qrka.ru',
      description:
        'Создайте штрих-код EAN-13, EAN-8, UPC-A, Code 128 или Code 39 онлайн. Проверка формата и бесплатное скачивание PNG или SVG.',
      changefreq: 'monthly',
      priority: 0.9,
      jsonLd: {
        mode: 'required',
        schemas: buildQrToolRouteSchemas(
          '/generator-shtrih-kodov',
          ruTranslations,
          'barcode-generator',
        ),
      },
    },
    'barcode-scanner': {
      path: '/skaner-shtrih-koda',
      title: 'Сканер штрихкодов онлайн по фото и с камеры - qrka.ru',
      description:
        'Распознайте EAN-13, EAN-8, UPC-A или Code 128 с фото и камеры. Локальная обработка в браузере, без загрузки изображения на сервер.',
      changefreq: 'monthly',
      priority: 0.9,
      jsonLd: {
        mode: 'required',
        schemas: buildQrToolRouteSchemas(
          '/skaner-shtrih-koda',
          ruTranslations,
          'barcode-scanner',
        ),
      },
    },
    'qr-scanner': {
      path: '/skaner-qr-koda',
      title: 'Сканер QR-кода онлайн по скриншоту, фото и с камеры - qrka.ru',
      description:
        'Сканируйте QR-код онлайн: вставьте скриншот через Ctrl+V, выберите фото или включите камеру. Всё работает в браузере, ссылки открываются только по вашему клику.',
      changefreq: 'monthly',
      priority: 0.9,
      jsonLd: {
        mode: 'required',
        schemas: buildQrToolRouteSchemas('/skaner-qr-koda', ruTranslations, 'qr-scanner'),
      },
    },
    blog: {
      path: '/blog',
      title: 'Статьи про QR-коды, печать и генерацию - qrka.ru',
      description:
        'Практические статьи про статические QR-коды, генерацию PNG и SVG, контраст, печать, Wi-Fi QR и выбор между статическим и динамическим QR.',
      changefreq: 'weekly',
      priority: 0.6,
      jsonLd: {
        mode: 'none',
        reason: 'Blog index page; article pages own their article JSON-LD.',
      },
    },
  },
}

export function seoFor(key: RouteKey, locale: Locale = DEFAULT_LOCALE): SeoProps {
  if (locale === 'en') {
    const route = seoRoutes.routes[key]
    const seoKey = key === 'qr-generator'
      ? 'qrGenerator'
      : key === 'barcode-generator'
        ? 'barcodeGenerator'
      : key === 'barcode-scanner'
        ? 'barcodeScanner'
      : key === 'qr-scanner'
        ? 'qrScanner'
        : key
    const localizedRoute = {
      ...route,
      ...enTranslations.seo[seoKey],
    }
    if (isQrToolRouteKey(key)) {
      localizedRoute.jsonLd = {
        mode: 'required',
        schemas: buildQrToolRouteSchemas(route.path, enTranslations, key),
      }
    }
    return toSeoProps(seoRoutes.domain, localizedRoute, {
      locale: 'en',
      defaultImage: branding.ogImage,
    })
  }

  return toSeoProps(seoRoutes.domain, seoRoutes.routes[key], { defaultImage: branding.ogImage })
}

function isQrToolRouteKey(key: RouteKey): key is QrToolRouteKey {
  return key !== 'index' && key !== 'blog'
}
